import type { BuildingBounds } from "building-types/building/buildingBounds";
import type { BuiltInSheetCameraKey, ViewportCameraKey } from "building-types/views/CameraKey";
import { DEFAULT_EYE_LEVEL } from "common/constants/camera";
import type { CameraPosition } from "common/types/cameraPosition";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { degreeToRadian } from "common/utils/math";
import { aabb2Copy } from "geom/aabb2";
import type { Vec3 } from "geom/vec3";
import { vec3Copy, vec3Normalize, vec3Result, vec3Sub } from "geom/vec3";
import { defaultExteriorElevationCamera } from "../elevation";
import type { PerspectiveDrawingCamera } from "../plans/drawingCameraTypes";
import { DrawingCameraType, drawingCameraFullViewAABB } from "../plans/drawingCameraTypes";

function buildingBoundsLookAtPosition(buildingBounds: BuildingBounds, lookAtZ: number): Vec3 {
  const { minX, minY, maxX, maxY } = buildingBounds.aabb;
  vec3Result[0] = (minX + maxX) * 0.5;
  vec3Result[1] = (minY + maxY) * 0.5;
  vec3Result[2] = lookAtZ;
  return vec3Result;
}

function cameraKeyToDirection(cameraKey: ViewportCameraKey | BuiltInSheetCameraKey): {
  direction: Vec3;
  lookAtZ: number;
} {
  switch (cameraKey) {
    case "topLeftPerspectiveCamera":
    case "frontLeftAerial":
      return { direction: [1, 1, -0.1], lookAtZ: 0 };
    case "topRightPerspectiveCamera":
    case "frontRightAerial":
      return { direction: [-1, 1, -0.5], lookAtZ: 0 };
    case "bottomLeftPerspectiveCamera":
    case "backLeftAerial":
      return { direction: [1, -1, -1], lookAtZ: 0 };
    case "bottomRightPerspectiveCamera":
    case "backRightAerial":
      return { direction: [-1, -1, -0.25], lookAtZ: 0 };
    case "defaultExteriorCamera":
    case "frontSidewalk":
      return {
        direction: [-defaultExteriorElevationCamera.x, -defaultExteriorElevationCamera.y, 0],
        lookAtZ: DEFAULT_EYE_LEVEL,
      };
    default:
      assertUnreachable(cameraKey);
  }
}

function buildingBoundsEye(
  buildingBounds: BuildingBounds,
  cameraKey: ViewportCameraKey | BuiltInSheetCameraKey,
  fovDegrees: number,
): Vec3 {
  const { direction, lookAtZ } = cameraKeyToDirection(cameraKey);
  const [dirX, dirY, dirZ] = vec3Normalize(direction[0], direction[1], direction[2]);

  const aabb = buildingBounds.aabb;

  const fovRads = degreeToRadian(fovDegrees);
  const [diagonalX, diagonalY, diagonalZ] = vec3Sub(aabb.maxX, aabb.maxY, aabb.maxZ, aabb.minX, aabb.minY, aabb.minZ);
  const [centerX, centerY, centerZ] = buildingBoundsLookAtPosition(buildingBounds, lookAtZ);

  const radius = Math.sqrt(diagonalX * diagonalX + diagonalY * diagonalY + diagonalZ * diagonalZ) * 0.5;
  const dist = (2 * radius) / Math.sin(fovRads * 0.5);

  const x = centerX - dirX * dist;
  const y = centerY - dirY * dist;
  const z = centerZ - dirZ * dist;

  return [x, y, z];
}

export function buildingBoundsGetDrawingCamera(
  buildingBounds: BuildingBounds,
  savedCameraFromElevation: CameraPosition | undefined,
  cameraKey: ViewportCameraKey | BuiltInSheetCameraKey,
  fovDegrees: number,
  aspect: number,
  near: number,
  far: number,
): PerspectiveDrawingCamera {
  const eye = savedCameraFromElevation
    ? ([savedCameraFromElevation.x, savedCameraFromElevation.y, savedCameraFromElevation.z] as Vec3)
    : buildingBoundsEye(buildingBounds, cameraKey, fovDegrees);

  const lookAtZ =
    savedCameraFromElevation || cameraKey === "defaultExteriorCamera" || cameraKey === "frontSidewalk"
      ? DEFAULT_EYE_LEVEL
      : 0;
  const target = buildingBoundsLookAtPosition(buildingBounds, lookAtZ);

  return {
    type: DrawingCameraType.PERSPECTIVE,
    eye: vec3Copy(eye),
    target: vec3Copy(target),
    up: [0, 0, 1],
    fovDegrees,
    aspect,
    near,
    far,
    viewCropAABB: aabb2Copy(drawingCameraFullViewAABB),
  };
}
